
import { defineComponent, onMounted, onBeforeUnmount, inject, ref, toRef, PropType } from "vue";
import { Modals } from "@models/enums";
import { ciTrackingService } from "@services/ci-tracking.service";
import useUserProfile from "@views/users/profiles/userProfileComposable";
import { useRouter } from "vue-router/composables";

export default defineComponent({
  name: "CiUsersProfileContactModal",
  props: {
    user: {
      type: Object as PropType<UsersV2.User>,
      default: () => ({}),
    },
  },
  setup(props) {
    const trackingUserType = inject<string>("trackingUserType", "");
    const modalId = Modals.USER_CONTACT_MODAL;
    const showPhoneNumber = ref(false);
    const router = useRouter();

    const { profileOwner } = useUserProfile(toRef(props, "user"));

    const handlePhoneClick = async () => {
      await ciTrackingService.trackClick("UserProfileContactDetailsPhone", props.user?.id.toString(), trackingUserType);

      // strip invalid characters from phone number,
      // use custom function to make testable (see profile cypress)
      window.setLocationHref(`tel:${props.user!.contact!.phone.replace(/[\s()-]+/gi, "")}`);
    };

    const handleCompanyNameClick = async () => {
      await ciTrackingService.trackClick(
        "UserProfileContactDetailsCompany",
        props.user?.id.toString(),
        trackingUserType
      );

      await router.push(props.user!.company!.url!);
    };

    const handleSendMessageClick = async () => {
      await ciTrackingService.trackClick(
        "ButtonSendMessageOnContactModal",
        props.user?.id.toString(),
        trackingUserType
      );
    };

    const handleModalShown = async () => {
      await ciTrackingService.trackView("UserProfileContactDetails", props.user?.id.toString(), trackingUserType);
    };

    onMounted(() => {
      window.setLocationHref = (url: string) => {
        window.location.href = url;
      };
    });

    onBeforeUnmount(() => {
      // reset function, may be affected by testing
      window.setLocationHref = (url: string) => {
        window.location.href = url;
      };
    });

    return {
      modalId,
      showPhoneNumber,
      profileOwner,
      handleCompanyNameClick,
      handleModalShown,
      handlePhoneClick,
      handleSendMessageClick,
    };
  },
  computed: {
    joinedAddress(): string {
      return [
        [this.user.location?.zipcode, this.user.location?.city].filter((v) => !!v).join(" "),
        this.user.location?.state_name,
        this.user.location?.country_name,
      ]
        .filter((v) => !!v)
        .join(", ");
    },
    contactComplete(): boolean {
      return (
        Boolean(this.user.contact?.phone) &&
        Boolean(this.user.company?.name) &&
        Boolean(this.user.location?.street) &&
        Boolean(this.user.location?.zipcode) &&
        Boolean(this.user.location?.city) &&
        Boolean(this.user.location?.country_name)
      );
    },
  },
});
